import { useAuth0 } from '@auth0/auth0-react';
import { useQuery } from '@tanstack/react-query';
import React from 'react';

import DatevBookingDataDisplay from '../../../components/DatevBookingDataDisplay';
import SplitPanelBookingLayout from '../../../components/invoice-preview/SplitPanelBookingLayout';
import { UploadedReceipt, LexOfficeBookingData, DatevBookingData } from '../../../types';
import { getBookingImportJobByReceiptId } from '../apis/bookingImportApi';
import { useBookingCardState } from '../hooks/useBookingCardState';
import { useReceiptActions } from '../hooks/useReceiptActions';

import BookingCardHeader from './BookingCardHeader';
import BookingDataForm from './BookingDataForm';
import BookingStatusDisplay from './BookingStatusDisplay';
import ImportFailedBanner from './ImportFailedBanner';

interface CollapsibleBookingCardProps {
  receipt: UploadedReceipt;
  isDisabled?: boolean;
  onRemove?: (receiptId: string) => void;
  isRemoving?: boolean;
}

const CollapsibleBookingCard: React.FC<CollapsibleBookingCardProps> = ({
  receipt,
  isDisabled = false,
  onRemove,
  isRemoving = false,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const state = useBookingCardState({ receipt });
  const { importReceipt, rejectReceipt } = useReceiptActions();

  const { data: importJob } = useQuery({
    queryKey: ['booking-import-job', 'from-receipt', receipt.id],
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      return getBookingImportJobByReceiptId(receipt.id, token);
    },
    enabled: receipt.status === 'import-failed',
  });

  const hasLexOfficeBookingData = !!receipt.lexOfficeBooking;
  const hasDatevBookingData =
    Array.isArray(receipt.datevBooking) &&
    receipt.datevBooking.length > 0;

  const handleImportBooking = async () => {
    state.setIsImportScheduling(true);
    let result;
    if (state.bookingSystem === 'datev') {
      const proposedBookingData = state.hasDatevChanges
        ? state.updatedDatevBookingData
        : undefined;
      result = await importReceipt(receipt.id, state.hasDatevChanges, proposedBookingData);
    } else {
      const hasChanges =
        JSON.stringify(state.updatedBookingData) !==
        JSON.stringify(state.originalBookingData);
      const proposedBookingData = hasChanges
        ? state.updatedBookingData
        : undefined;
      result = await importReceipt(receipt.id, hasChanges, proposedBookingData);
    }
    if (!result) {
      state.setIsImportScheduling(false);
    }
  };

  const handleRejectBooking = async () => {
    state.setIsRejecting(true);
    const success = await rejectReceipt(receipt.id);
    if (!success) {
      state.setIsRejecting(false);
    }
  };

  const handleSaveBookingData = (updatedData: LexOfficeBookingData) => {
    state.setUpdatedBookingData(updatedData);
  };

  const handleRestoreOriginal = () => {
    state.setUpdatedBookingData(state.originalBookingData);
  };

  const handleSaveDatevBookingData = (updatedData: DatevBookingData[]) => {
    // Save to local state only (temporary)
    // User must click header "Save" button to persist to database
    state.setUpdatedDatevBookingData(updatedData);
  };

  const handleRestoreDatevOriginal = () => {
    state.setUpdatedDatevBookingData(state.originalDatevBookingData);
  };

  return (
    <div className='border border-gray-200 rounded-lg bg-white shadow-sm'>
      {/* Header */}
      <BookingCardHeader
        receipt={receipt}
        state={state}
        onToggleExpanded={() => state.setIsExpanded(!state.isExpanded)}
        onImport={handleImportBooking}
        onReject={handleRejectBooking}
        onRemove={onRemove}
        isRemoving={isRemoving}
        isDisabled={isDisabled}
      />

      {/* Import Failed Message - Show below header when status is import-failed */}
      {receipt.status === 'import-failed' && (
        <ImportFailedBanner errorMessage={importJob?.errorMessage || receipt.errorMessage} />
      )}

      {/* Expanded Content */}
      {state.isExpanded && (
        <div className='p-4 pt-0 border-t border-gray-100'>
          <SplitPanelBookingLayout receipt={receipt}>
            {hasLexOfficeBookingData && state.updatedBookingData ? (
              <>
                {/* LexOffice Booking Data Form */}
                <BookingDataForm
                  bookingData={state.updatedBookingData}
                  onSave={
                    !state.isImported &&
                    !state.isRejected &&
                    !state.hasImportingStatus
                      ? handleSaveBookingData
                      : undefined
                  }
                  isEditing={state.isEditing}
                  onEditModeChange={state.setIsEditing}
                  showValidation={state.showValidation}
                  hasChanges={state.hasChanges}
                  onRestore={
                    !state.isImported &&
                    !state.isRejected &&
                    !state.hasImportingStatus
                      ? handleRestoreOriginal
                      : undefined
                  }
                />

                {/* Status Display or Warning notification */}
                {!state.isEditing && (
                  <div className='mt-4 pt-4'>
                    <BookingStatusDisplay
                      isImported={state.isImported}
                      isRejected={state.isRejected}
                      hasChanges={receipt.status === 'modified'}
                    />
                  </div>
                )}
              </>
            ) : hasDatevBookingData && receipt.datevBooking ? (
              <DatevBookingDataDisplay
                bookingData={state.updatedDatevBookingData || receipt.datevBooking}
                originalBookingData={state.originalDatevBookingData}
                onSave={
                  !state.isImported &&
                  !state.isRejected &&
                  !state.hasImportingStatus
                    ? handleSaveDatevBookingData
                    : undefined
                }
                onRestore={
                  !state.isImported &&
                  !state.isRejected &&
                  !state.hasImportingStatus
                    ? handleRestoreDatevOriginal
                    : undefined
                }
                onEditModeChange={state.setIsEditing}
                showHeader={false}
              />
            ) : (
              <div className='text-sm text-gray-500 italic py-4'>
                No booking data available
              </div>
            )}
          </SplitPanelBookingLayout>
        </div>
      )}
    </div>
  );
};

export default CollapsibleBookingCard;
